import React from 'react';
import { get } from '../../../shared/http/request';
import {
    Alert,
    Button,
    Col,
    ControlLabel,
    Form,
    FormControl,
    FormGroup,
    Glyphicon,
    HelpBlock,
    Panel,
    Row,
} from 'react-bootstrap';
import Datetime from 'react-datetime';
import { ABNORMALITIES, RECOVERY_ACTIONS, I18N } from '../../../constants';
import moment from 'moment';
import queryString from 'query-string';
import update from 'immutability-helper';
import { createAbnormalityKPIBreadcrumb } from '../../../shared/utils/breadcrumbs';

const API_BASE_URL = '/api/reports';

export default class AbnormalityKPI extends React.PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            errors: {},
            logisticPartners: [],
            logisticPoints: [],
            search: {
                routeId: '',
                logisticsPointOrAll: 'ALL',
                logisticsPartner: '',
                abnormalityType: '',
                recoveryActionType: '',
                from: moment(),
                to: moment().add(1, 'months'),
            },
        };
    }

    componentDidMount() {
        get(`${API_BASE_URL}/logistic-partners`).then(
            logisticPartners =>
                this.setState({
                    logisticPartners: logisticPartners.logisticPartnerNames
                        ? logisticPartners.logisticPartnerNames.sort()
                        : [],
                }),
            error => console.log('Error while getting logistic partners', error)
        );
        get(`${API_BASE_URL}/logistic-points`).then(
            logisticPoints => this.setState({ logisticPoints }),
            error => console.log('Error while getting logistic points', error)
        );
    }

    handleChange = e => {
        this.setState({
            search: update(this.state.search, {
                [e.target.name]: { $set: e.target.value },
            }),
        });

        const field = e.target.name;
        if (this.state.errors[field] !== undefined) {
            this.setState({
                errors: update(this.state.errors, {
                    [field]: { $set: undefined },
                }),
            });
        }
    };

    handleDateChange = name => date => {
        this.setState({
            search: update(
                this.state.search,
                {
                    [name]: { $set: date },
                },
                () => {
                    if (
                        this.state.errors[name] !== undefined &&
                        this.isValidRange(this.state.search.from, this.state.search.to)
                    ) {
                        this.setState({
                            errors: update(this.state.errors, {
                                [name]: { $set: undefined },
                            }),
                        });
                    }
                }
            ),
        });
    };

    isValidRange = (from, to) => {
        const duration = moment.duration(moment(to).diff(moment(from)));
        return Math.abs(duration.asDays()) <= 35;
    };

    handleValidToDate = currentDate => {
        return (
            currentDate.isAfter(this.state.search.from) &&
            this.isValidRange(currentDate, this.state.search.from)
        );
    };

    validateForm = e => {
        const { from, to, logisticsPointOrAll } = this.state.search;
        let error = false;
        let errors = {};

        if (logisticsPointOrAll === '') {
            error = true;
            errors.logisticsPointOrAll = 'A logistics point is required.';
        }

        if (!this.isValidRange(from, to)) {
            error = true;
            errors.to = 'To date must be within 35 days of the from date.';
        }

        if (error) {
            this.setState(() => ({ errors }));
            e.preventDefault();
        } else {
            this.setState(() => ({ errors: {} }));
        }
    };

    prepareFormDataForPost = form => {
        const search = update(form, {
            from: {
                $set: form.from ? moment(form.from).format('YYYY-MM-DD') : '',
            },
            to: {
                $set: form.from ? moment(form.to).format('YYYY-MM-DD') : '',
            },
        });

        return queryString.stringify(search);
    };

    render() {
        const { search, errors } = this.state;

        return (
            <Col sm={12}>
                {createAbnormalityKPIBreadcrumb()}
                <Form horizontal>
                    <Row>
                        <Col sm={6} className="no-gutter">
                            <Panel header="Abnormality / Recovery action">
                                {errors.type && <Alert bsStyle="danger">{errors.type}</Alert>}
                                <FormGroup controlId="abnormalityType">
                                    <Col sm={4} componentClass={ControlLabel}>
                                        Abnormality type
                                    </Col>
                                    <Col sm={8}>
                                        <FormControl
                                            componentClass="select"
                                            name="abnormalityType"
                                            value={search.abnormalityType}
                                            onChange={this.handleChange}
                                        >
                                            <option value="" disabled>
                                                ...
                                            </option>
                                            {Object.keys(ABNORMALITIES).map(abnormality => (
                                                <option key={abnormality} value={abnormality}>
                                                    {I18N.ABNORMALITIES[abnormality]}
                                                </option>
                                            ))}
                                        </FormControl>
                                    </Col>
                                </FormGroup>
                                <FormGroup
                                    controlId="recoveryActionType"
                                    className="no-vertical-margin"
                                >
                                    <Col sm={4} componentClass={ControlLabel}>
                                        Recovery action type
                                    </Col>
                                    <Col sm={8}>
                                        <FormControl
                                            componentClass="select"
                                            name="recoveryActionType"
                                            value={search.recoveryActionType}
                                            onChange={this.handleChange}
                                        >
                                            <option value="" disabled>
                                                ...
                                            </option>
                                            {Object.keys(RECOVERY_ACTIONS).map(recoveryAction => (
                                                <option key={recoveryAction} value={recoveryAction}>
                                                    {I18N.RECOVERY_ACTIONS[recoveryAction]}
                                                </option>
                                            ))}
                                        </FormControl>
                                    </Col>
                                </FormGroup>
                            </Panel>
                        </Col>
                    </Row>
                    <Row>
                        <Col sm={6} className="no-gutter">
                            <Panel header="Logistics">
                                <FormGroup controlId="logisticsPointOrAll">
                                    <Col sm={4} componentClass={ControlLabel}>
                                        Logistics point *
                                    </Col>
                                    <Col sm={8}>
                                        <FormControl
                                            componentClass="select"
                                            name="logisticsPointOrAll"
                                            value={search.logisticsPointOrAll}
                                            onChange={this.handleChange}
                                        >
                                            <option value="" disabled>
                                                ...
                                            </option>
                                            {Object.keys(this.state.logisticPoints).map(
                                                logisticPointType => (
                                                    <optgroup
                                                        key={logisticPointType}
                                                        label={
                                                            I18N.LOGISTIC_POINT_TYPE[
                                                                logisticPointType
                                                            ]
                                                        }
                                                    >
                                                        {this.state.logisticPoints[
                                                            logisticPointType
                                                        ].map(point => (
                                                            <option key={point} value={point}>
                                                                {point}
                                                            </option>
                                                        ))}
                                                    </optgroup>
                                                )
                                            )}
                                            <option value="ALL">All logistic points</option>
                                        </FormControl>
                                        {errors.logisticsPointOrAll && (
                                            <HelpBlock>
                                                <span className="text-danger">
                                                    {errors.logisticsPointOrAll}
                                                </span>
                                            </HelpBlock>
                                        )}
                                    </Col>
                                </FormGroup>
                                <FormGroup controlId="logisticsPartner">
                                    <Col sm={4} componentClass={ControlLabel}>
                                        Logistic partner
                                    </Col>
                                    <Col sm={8}>
                                        <FormControl
                                            componentClass="select"
                                            name="logisticsPartner"
                                            value={search.logisticsPartner}
                                            onChange={this.handleChange}
                                        >
                                            <option value="" disabled>
                                                ...
                                            </option>
                                            {this.state.logisticPartners.map(partner => (
                                                <option key={partner} value={partner}>
                                                    {partner}
                                                </option>
                                            ))}
                                        </FormControl>
                                    </Col>
                                </FormGroup>
                                <FormGroup controlId="routeId" className="no-vertical-margin">
                                    <Col sm={4} componentClass={ControlLabel}>
                                        Route code
                                    </Col>
                                    <Col sm={8}>
                                        <FormControl
                                            type="select"
                                            name="routeId"
                                            value={search.routeId}
                                            placeholder="..."
                                            onChange={this.handleChange}
                                        />
                                    </Col>
                                </FormGroup>
                            </Panel>
                        </Col>
                    </Row>
                    <Row>
                        <Col sm={6} className="no-gutter">
                            <Panel header="Timeframe" className="no-vertical-margin">
                                <FormGroup controlId="from">
                                    <Col sm={4} componentClass={ControlLabel}>
                                        From *
                                    </Col>
                                    <Col sm={8}>
                                        <Datetime
                                            value={search.from}
                                            onChange={this.handleDateChange('from')}
                                            dateFormat="DD-MM-YYYY"
                                            timeFormat={false}
                                            inputProps={{
                                                maxLength: 255,
                                                placeholder: '...',
                                            }}
                                            closeOnSelect
                                        />
                                    </Col>
                                </FormGroup>
                                <FormGroup controlId="to" className="no-vertical-margin">
                                    <Col sm={4} componentClass={ControlLabel}>
                                        To *
                                    </Col>
                                    <Col sm={8}>
                                        <Datetime
                                            value={search.to}
                                            onChange={this.handleDateChange('to')}
                                            isValidDate={this.handleValidToDate}
                                            dateFormat="DD-MM-YYYY"
                                            timeFormat={false}
                                            inputProps={{
                                                maxLength: 255,
                                                placeholder: '...',
                                            }}
                                            closeOnSelect
                                        />
                                        {errors.to && (
                                            <HelpBlock>
                                                <span className="text-danger">{errors.to}</span>
                                            </HelpBlock>
                                        )}
                                        <HelpBlock>
                                            <Glyphicon glyph="info-sign" className="text-primary" />{' '}
                                            From - To range is limited to 35 days.
                                        </HelpBlock>
                                    </Col>
                                </FormGroup>
                            </Panel>
                        </Col>
                        <Col sm={12} className="no-gutter">
                            <p style={{ paddingTop: '10px' }}>
                                <strong>Fields marked with * are required.</strong>
                            </p>
                        </Col>
                        <Col sm={12}>
                            <FormGroup controlId="submit">
                                <Button
                                    bsStyle={'success'}
                                    onClick={this.validateForm}
                                    href={`${API_BASE_URL}/download-abnormality-report/FTS_Abnormality_KPI_${moment().format(
                                        'DD_MM_YYYY-H_mm'
                                    )}?${this.prepareFormDataForPost(this.state.search)}`}
                                >
                                    Download KPI report
                                </Button>
                            </FormGroup>
                        </Col>
                    </Row>
                </Form>
            </Col>
        );
    }
}
